import React, { Component } from 'react';
import { Card, CardBody, CardHeader, Badge, Alert } from 'reactstrap';
import moment from 'moment';
import axios from '../../Shared/auth-header';
import AuthService from '../../Auth/AuthService';
import PermissionFeature from '../../Permissions/PermissionFeature';

/**
 * RecentAlertsPanel Component
 *
 * Displays recent email alerts from the last 24 hours.
 * Shows alert type, severity, email status, and dashboard view status.
 *
 * Props:
 * - hours: number (optional) - Number of hours to look back (default: 24)
 * - refreshInterval: number (optional) - Auto-refresh interval in ms (default: 30000)
 */
class RecentAlertsPanel extends Component {
    constructor(props) {
        super(props);

        this.state = {
            alerts: [],
            loading: true,
            error: null,
            hoursToShow: props.hours || 24
        };

        this.refreshInterval = null;
    }

    componentDidMount() {
        // Check permission before loading data
        const canViewAlerts = AuthService.canView(PermissionFeature.MonitoringDashboardViewAlerts);

        if (!canViewAlerts) {
            this.setState({ loading: false });
            return; // Don't load data if user doesn't have permission
        }

        this.loadAlerts();

        // Set up auto-refresh (default: every 30 seconds)
        const refreshInterval = this.props.refreshInterval || 30000;
        this.refreshInterval = setInterval(() => {
            this.loadAlerts(true); // Silent refresh (don't show loading spinner)
        }, refreshInterval);
    }

    componentDidUpdate(prevProps) {
        // If hours prop changed from parent, update state and reload data
        if (prevProps.hours !== this.props.hours) {
            this.setState({
                hoursToShow: this.props.hours
            }, () => {
                this.loadAlerts();
            });
        }
    }

    componentWillUnmount() {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
        }
    }

    loadAlerts = async (silent = false) => {
        try {
            if (!silent) {
                this.setState({ loading: true, error: null });
            }

            const response = await axios.get(
                `/api/monitoring/alerts/recent?hours=${this.state.hoursToShow}`
            );

            const alerts = (response.data && response.data.Data) || (response.data && response.data.data) || response.data || [];

            this.setState({
                alerts: alerts,
                loading: false,
                error: null
            });

            // Notify parent of alert count (for badge updates)
            if (this.props.onCountUpdate) {
                this.props.onCountUpdate(alerts.length);
            }
        } catch (error) {
            console.error('Failed to load recent alerts:', error);
            this.setState({
                error: 'Failed to load recent alerts. Please try again later.',
                loading: false
            });
        }
    };

    getSeverityColor = (severity) => {
        switch (severity) {
            case 'Critical':
                return 'danger';
            case 'Warning':
                return 'warning';
            case 'Info':
                return 'info';
            default:
                return 'secondary';
        }
    };

    render() {
        const { alerts, loading, error } = this.state;
        const { standalone = true } = this.props;

        // Check if user has permission to view alerts
        const canViewAlerts = AuthService.canView(PermissionFeature.MonitoringDashboardViewAlerts);

        if (!canViewAlerts) {
            // Hide component with display:none (visible in dev tools, hidden in UI)
            return <div style={{ display: 'none' }} data-component="RecentAlertsPanel" data-reason="No ViewAlerts permission" />;
        }

        const content = (
            <div className={standalone ? "" : "p-3"}>
                    {error && (
                        <div className="alert alert-danger mb-0">
                            <i className="fa fa-exclamation-triangle mr-2"></i>
                            {error}
                        </div>
                    )}

                    {!error && !loading && alerts.length === 0 && (
                        <div className="text-center text-muted py-3">
                            <i className="fa fa-check-circle fa-2x mb-2"></i>
                            <p className="mb-0">No recent alerts in the selected time range</p>
                        </div>
                    )}

                    {!error && alerts.length > 0 && (
                        <div className="alerts-list" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            {alerts.map((alert) => (
                                <div
                                    key={alert.alertId}
                                    className="alert-item border-bottom pb-2 mb-2 last:border-0"
                                >
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div className="flex-grow-1">
                                            <div className="d-flex align-items-center mb-1">
                                                <Badge
                                                    color={this.getSeverityColor(alert.severity)}
                                                    className="mr-2"
                                                >
                                                    {alert.alertType}
                                                </Badge>
                                                <strong>{alert.serviceName}</strong>
                                            </div>

                                            <div className="text-muted small mb-1">
                                                {moment.utc(alert.createdDate).local().format('MMM D, h:mm A')}
                                                {' • '}
                                                {alert.emailSent === 1 ? (
                                                    <span className="text-success">
                                                        <i className="fa fa-check-circle"></i> Email sent
                                                    </span>
                                                ) : alert.emailError && alert.emailError.toLowerCase().includes('disabled') ? (
                                                    <span className="text-warning">
                                                        <i className="fa fa-database"></i> Email logged (sending disabled)
                                                    </span>
                                                ) : (
                                                    <span className="text-danger">
                                                        <i className="fa fa-times-circle"></i> Email failed
                                                        {alert.emailError && (
                                                            <span
                                                                className="ml-1"
                                                                title={alert.emailError}
                                                            >
                                                                (hover for details)
                                                            </span>
                                                        )}
                                                    </span>
                                                )}
                                                {alert.dashboardViewed === 1 && (
                                                    <span className="ml-2 text-info">
                                                        <i className="fa fa-eye"></i> Viewed
                                                    </span>
                                                )}
                                            </div>

                                            {alert.alertMessage && (
                                                <div className="small text-secondary">
                                                    {alert.alertMessage.length > 100
                                                        ? `${alert.alertMessage.substring(0, 100)}...`
                                                        : alert.alertMessage}
                                                </div>
                                            )}
                                        </div>

                                        <div className="ml-3">
                                            <Badge
                                                color={this.getSeverityColor(alert.severity)}
                                                pill
                                            >
                                                {alert.severity}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
            </div>
        );

        if (standalone) {
            return (
                <Card className="mb-3 recent-alerts-panel">
                    <CardHeader className="bg-light d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                            <i className="fa fa-envelope mr-2"></i>
                            Recent Alerts (Last 24 Hours)
                        </h5>
                        {loading && (
                            <small className="text-muted">
                                <i className="fa fa-spinner fa-spin"></i> Loading...
                            </small>
                        )}
                    </CardHeader>
                    <CardBody>
                        {content}
                    </CardBody>
                </Card>
            );
        }

        return content;
    }
}

export default RecentAlertsPanel;
